// LENNON MANAGEMENT — Creator management, partnerships & business development.
// lennonmgmt.com is the creator side of LENNON. The corporate/AI arm lives at lennon.global.
// This file is the single source of truth for site copy.

export const COMPANY = {
  name: "Lennon",
  full: "LENNON — Creator Management + Global Creator Partnerships",
  short: "LENNON",
  division: "LENNON MANAGEMENT",
  divisionShort: "Management",
  parentTagline: "One brand. Two divisions. One creator ecosystem.",
  hero: {
    line1: "Creators aren't",
    line2: "media buys.",
    line3: "They are culture.",
  },
  tagline:
    "Selective creator management, partnerships and business development for creators building lasting businesses.",
  ecosystem:
    "LENNON MANAGEMENT is the creator-facing side of LENNON, representing a selective roster of creators and building long-term careers, partnerships and businesses.",
  philosophy:
    "Creators are not interchangeable placements. Their value comes from the relationship they've built with their audience. LENNON works from that relationship first.",
  established: 2010,
  yearsLabel: "15+ years",
  location: "Los Angeles · Dubai · Global",
  studios: [
    { city: "Los Angeles", region: "California, USA", note: "Headquarters" },
    { city: "Dubai", region: "United Arab Emirates", note: "MENA" },
    { city: "Global", region: "Remote engagements", note: "Worldwide" },
  ],
  email: "hello@lennonmgmt.com",
  companiesUrl: "https://www.lennon.global",
  socials: {
    instagram: "https://www.instagram.com/lennonmgmt/",
    linkedin: "https://www.linkedin.com/in/naomilennon/",
  },
};

// The two sides of the LENNON business — displayed together so visitors
// understand they are complementary divisions, not separate companies.
export const DIVISIONS = [
  {
    key: "management",
    name: "LENNON MANAGEMENT",
    tag: "Creator-facing",
    lead: "Selective creator management, partnerships and business development for creators building lasting businesses.",
    bullets: [
      "Creator management",
      "Creator partnerships",
      "Creator business development",
      "Creator strategy",
    ],
    href: "/creators",
    hrefLabel: "This is Lennon Management",
    isCurrent: true,
  },
  {
    key: "global",
    name: "LENNON GLOBAL",
    tag: "Company-facing",
    lead: "Creator partnerships, strategy and execution for B2B companies moving at the speed of AI.",
    bullets: [
      "Enterprise creator programs",
      "AI-company creator strategy",
      "Platform partnerships",
      "Global creator infrastructure",
    ],
    href: "https://www.lennon.global",
    hrefLabel: "Visit Lennon Global",
    isCurrent: false,
  },
];

// Headline proof — displayed on Home. These are the only quantitative
// claims allowed on the site. Do not invent additional numbers.
export const PROOF_STATS = [
  { v: "13+ Years", l: "Building creator businesses" },
  { v: "5,000+", l: "Creator partnerships" },
  { v: "$45M+", l: "Creator partnership value generated" },
];

// Compact three-service teaser used on Home and as the /creators pillars.
export const SERVICES_SHORT = [
  {
    n: "01",
    t: "Creator Management",
    b: "Selective representation and long-term career and business development.",
  },
  {
    n: "02",
    t: "Creator Partnerships",
    b: "Brand partnerships, negotiation, deal strategy and commercial relationships.",
  },
  {
    n: "03",
    t: "Creator Business Development",
    b: "Products, commerce, publishing, IP and media that extend a creator's business beyond content.",
  },
];

// Three-pillar service catalogue — drives the /creators page.
export const SERVICES = [
  {
    n: "01",
    t: "Creator Management",
    lead:
      "Selective representation and long-term career and business development. We work deeply with a small number of creators, not broadly with many.",
    items: [
      "Selective, long-term representation",
      "Career strategy & deal negotiation",
      "Audience & brand development",
      "Business model design",
      "Team, legal and financial coordination",
    ],
  },
  {
    n: "02",
    t: "Creator Partnerships",
    lead:
      "Brand partnerships, negotiation, deal strategy and commercial relationships. Built as multi-year partnerships, not one-off placements.",
    items: [
      "Brand partnership strategy",
      "Deal structure & negotiation",
      "Casting & creator-brand fit",
      "Long-term commercial relationships",
    ],
  },
  {
    n: "03",
    t: "Creator Business Development",
    lead:
      "Products, commerce, publishing, IP, media and other opportunities that extend a creator's business beyond content.",
    items: [
      "Books & publishing partnerships",
      "Product lines & retail collaborations",
      "Licensing & IP expansion",
      "Production & entertainment opportunities",
    ],
  },
];

// Homepage timeline — trimmed to the four anchor moments. The fuller
// history (Zeitgeist, China, Tiësto/Vegas, Turkuaz) lives on the About page.
export const TIMELINE_SHORT = [
  {
    year: "2009",
    title: "YouTube Partner Program",
    body:
      "Working with emerging digital creators at the beginning of the YouTube era, before \u201Ccreator\u201D was a category.",
  },
  {
    year: "2010",
    title: "Lennon Management — Founded",
    body:
      "Built an early roster including RCLBeauty101, The Fine Brothers, Racka Racka and BlackBoxTV.",
  },
  {
    year: "2019",
    title: "Lennon Management — Acquired",
    body:
      "Lennon Management was acquired. Naomi continued building creator businesses under the LENNON name.",
  },
  {
    year: "Today",
    title: "Creator management, continued",
    body:
      "Naomi continues to selectively represent creators through LENNON MANAGEMENT while leading LENNON's broader creator partnerships business.",
  },
];

// Career credibility timeline — Naomi's arc, preserved. Softens the 2019
// acquisition into part of the story, not the end of the business.
export const TIMELINE = [
  {
    year: "2009",
    title: "YouTube Partner Program",
    body:
      "Among the first creator managers in the world — working with emerging digital creators at the beginning of the YouTube era, before 'creator' was a category.",
  },
  {
    year: "2010",
    title: "Lennon Management — Founded",
    body:
      "Launched the firm. Built an early roster including RCLBeauty101 (#1 most-viewed female channel worldwide), The Fine Brothers, Racka Racka and BlackBoxTV.",
    image: "/photos/mac.jpg",
    imageAlt: "Naomi Lennon at MAC Cosmetics booth — early talent-brand partnerships era",
  },
  {
    year: "2016",
    title: "Google Zeitgeist · Great Expectations",
    body:
      "In the room at Google Zeitgeist — the invitation-only summit where the platforms and operators shaping the next decade of the internet meet.",
    image: "/photos/zeitgeist.jpg",
    imageAlt: "Google Zeitgeist '16 — Great Expectations stage",
  },
  {
    year: "2017",
    title: "Talent goes global",
    body:
      "RCLBeauty101 staged at RedBang's World Internet Celebrity summit in China — proof the firm's creators were no longer a US-only story.",
    image: "/photos/china.jpg",
    imageAlt: "RCLBeauty101 / Rachel Levin on stage at RedBang World Internet Celebrity, China",
  },
  {
    year: "2018",
    title: "Tiësto · Las Vegas",
    body:
      "On location in Las Vegas for Tiësto's music video production with Universal Music — the firm crossing fully into the major-label music world.",
    image: "/photos/vegas.jpg",
    imageAlt: "On location in Las Vegas — Tiësto music video shoot with Universal Music",
  },
  {
    year: "2019",
    title: "Lennon Management — Acquired",
    body:
      "Lennon Management was acquired. Naomi continued building creator businesses under the LENNON name.",
  },
  {
    year: "2022 —",
    title: "Turkuaz Kitchen · Anthropologie · NYT Best Sellers",
    body:
      "A full creator-to-commerce arc with Betül Tunç — exclusive Anthropologie homeware at Rockefeller Center and a New York Times Best Sellers cookbook with Ten Speed Press.",
  },
  {
    year: "Today",
    title: "Creator management, continued",
    body:
      "Naomi continues to selectively represent creators through LENNON MANAGEMENT while leading LENNON's broader creator partnerships business.",
  },
];

// Case studies — the portfolio of creator businesses. Metrics kept honest.
export const CASES = [
  {
    slug: "turkuaz-kitchen",
    title: "Turkuaz Kitchen — Betül Tunç",
    role: "Creator → Business",
    period: "2022 — present",
    summary:
      "Built a creator-to-commerce business with Betül Tunç of Turkuaz Kitchen, including an Anthropologie homeware collection and a New York Times Best Sellers cookbook with Ten Speed Press.",
    metrics: [
      { v: "10M+", l: "Followers across platforms" },
      { v: "NYT", l: "Best Sellers list — Nov 2024" },
      { v: "Anthropologie", l: "Exclusive homeware launch" },
    ],
    image:
      "https://images.ctfassets.net/5de70he6op10/1scfclXH66YIpADJfmon9C/1e54c3709cdce10572794499e6164378/588487708-022224_betultunc_contenthub_ls_04.jpg?w=2000&q=85&fm=jpg",
    imageAlt: "Betül Tunç at the Anthropologie Rockefeller Center launch",
  },
  {
    slug: "rcl-beauty",
    title: "RCLBeauty101 — Rachel Levin",
    role: "Creator → Brand",
    period: "2010 — 2019",
    summary:
      "Signed Rachel Levin as a teenager and built one of the most loyal beauty communities on the internet — from flagship brand partnerships to her own cosmetics business.",
    metrics: [
      { v: "14M+", l: "YouTube subscribers" },
      { v: "#1", l: "Most-viewed female channel worldwide" },
      { v: "RCLÓ Cosmetics", l: "Creator-owned cosmetics line" },
    ],
    image: "/photos/rcl-cosmetics.jpg",
    imageAlt: "RCLÓ Cosmetics booth by RCLBeauty — Rachel Levin's cosmetics line at retail event",
  },
  {
    slug: "rackaracka",
    title: "Racka Racka — Danny & Michael Philippou",
    role: "Creator → Film",
    period: "2013 — 2019",
    summary:
      "I discovered the brothers and helped secure brand financing to develop their filmmaking. Commercial work became a pathway into feature filmmaking.",
    metrics: [
      { v: "Brand-financed", l: "Commercial → Feature pipeline" },
      { v: "Variety", l: "Feature coverage" },
      { v: "Forbes", l: "Industry profile" },
    ],
    image: "/photos/racka-racka-set.jpg",
    imageAlt: "On set with the Philippou brothers — brand-financed production → feature filmmaking",
  },
  {
    slug: "fine-bros",
    title: "The Fine Brothers — Benny & Rafi Fine",
    role: "Creator → Studio",
    period: "2010 — 2015",
    summary:
      "Represented Benny and Rafi Fine as they scaled the React franchise into one of YouTube's most-watched formats and one of the earliest creator-owned studios — a template for the creator-as-studio model.",
    metrics: [
      { v: "React", l: "Multi-series franchise" },
      { v: "Creator-owned", l: "Studio model" },
      { v: "Early", l: "Creator-to-television pipeline" },
    ],
    image:
      "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=2000&q=85",
    imageAlt: "Studio production, creator-owned format",
  },
  {
    slug: "blackboxtv",
    title: "BlackBoxTV — Tony Valenzuela",
    role: "Creator → Scripted",
    period: "2017 — 2019",
    summary:
      "Executive produced Tony Valenzuela's BlackBoxTV with New Form Digital — a creator-led scripted anthology that pre-dated the streamer push into creator IP. A blueprint for premium digital programming from one of YouTube's original horror auteurs.",
    metrics: [
      { v: "EP", l: "Executive Producer credit" },
      { v: "New Form", l: "Premium digital studio" },
      { v: "Scripted", l: "Creator-led IP" },
    ],
    image:
      "https://upload.wikimedia.org/wikipedia/commons/1/19/Blackboxtv_presents_poster_image.jpg",
    imageAlt: "BlackBoxTV Presents poster",
  },
];

// Roster / creators worked with — shown on /creators page.
export const CREATORS = [
  { name: "Betül Tunç", handle: "Turkuaz Kitchen", note: "NYT Best Sellers · Anthropologie" },
  { name: "Rachel Levin", handle: "RCLBeauty101", note: "#1 most-viewed female channel · RCLÓ Cosmetics" },
  { name: "Danny & Michael Philippou", handle: "Racka Racka", note: "YouTube → Feature film" },
  { name: "Benny & Rafi Fine", handle: "The Fine Brothers", note: "React franchise · Creator studio" },
  { name: "Tony Valenzuela", handle: "BlackBoxTV", note: "Scripted horror anthology" },
];

// Brand partners — real brands the firm has worked with.
export const BRANDS = [
  "P&G",
  "Anthropologie",
  "McDonald's",
  "PepsiCo",
  "COVERGIRL",
  "Ford",
  "Samsung",
  "KIA",
  "Unilever",
  "Tarte",
  "AT&T",
  "Universal Music",
];

export const BRANDS_ALL = [
  ...BRANDS,
  "AliExpress",
  "Ten Speed Press",
  "New Form Digital",
  "Meta",
  "YouTube",
];

export const PRESS = [
  {
    outlet: "Net Influencer",
    title: "Lennon Management And The Long View Of Creator Careers",
    url: "https://www.netinfluencer.com/lennon-management-and-the-long-view-of-creator-careers/",
    year: "2026",
    pull:
      "\"Brand deals are not the business. They are infrastructure.\" — A profile of Naomi Lennon and the selective model behind 15 years of creator careers.",
  },
  {
    outlet: "The New York Times",
    title: "Best Sellers — Advice, How-To & Miscellaneous: Turkuaz Kitchen",
    url: "https://www.nytimes.com/books/best-sellers/2024/11/24/advice-how-to-and-miscellaneous/",
    year: "2024",
    pull:
      "Lennon-repped creator Betül Tunç debuts on the NYT Best Sellers list with the Turkuaz Kitchen cookbook (Ten Speed Press).",
  },
  {
    outlet: "Anthropologie",
    title: "Behind the Design — Meet Betül Tunç of Turkuaz Kitchen",
    url: "https://www.anthropologie.com/betul-tunc-collab",
    year: "2024",
    pull:
      "Anthropologie features the exclusive Turkuaz Kitchen × Anthropologie homeware collaboration at Rockefeller Center.",
  },
  {
    outlet: "Business Insider",
    title: "The Top Talent Managers for YouTube Creators",
    url: "https://www.businessinsider.com/top-talent-managers-of-youtube-creators-influencers-power-list-2019-10",
    year: "2019",
    pull:
      "Naomi Lennon named to Business Insider's power list of the industry's leading creator talent managers.",
  },
  {
    outlet: "Forbes",
    title: "Rachel Levin: A YouTuber With A Positive Message",
    url: "https://www.forbes.com/sites/tomward/2018/01/26/rachel-levin-a-youtuber-with-a-postive-message/",
    year: "2018",
    pull: "Forbes spotlight on Lennon Management client Rachel Levin (RCLBeauty101).",
  },
  {
    outlet: "Variety",
    title: "Racka Racka: Australian Brothers' Videos Lead to Film Deal",
    url: "https://variety.com/2016/digital/news/racka-racka-youtube-film-deal-1201797416/",
    year: "2016",
    pull:
      "Two Lennon-managed YouTube creators sign a feature film deal — covered by Variety.",
  },
];

export const RECOGNITION = ["NYT Bestseller", "Streamy Awards", "Shorty Awards", "Webby"];

export const IN_THE_ROOM = [
  { src: "/photos/streamys.jpg", alt: "Streamy Awards red carpet", caption: "Streamy Awards", note: "Industry recognition · VH1" },
  { src: "/photos/aliexpress.jpg", alt: "AliExpress event in China", caption: "AliExpress", note: "China · Brand activation" },
  { src: "/photos/onset.jpg", alt: "On set production", caption: "On set", note: "Production · Creator IP" },
  { src: "/photos/conference.jpg", alt: "Conference keynote", caption: "Conference", note: "Keynote · Industry stage" },
  { src: "/photos/vidcon.jpg", alt: "VidCon Hall B entrance crowd — YouTube, KIA", caption: "VidCon", note: "Hall B · YouTube · KIA" },
  { src: "/photos/roundtable.jpg", alt: "Industry roundtable", caption: "Industry roundtable", note: "Technology Pavilion" },
  { src: "/photos/editorial.jpg", alt: "Editorial portrait of Naomi Lennon", caption: "Editorial", note: "Profile · Los Angeles" },
];

export const FOUNDER = {
  name: "Naomi Lennon",
  title: "Founder",
  image: "/naomi-lennon.jpg",
  bio: [
    "Naomi Lennon was one of the first creator managers in the world — building creator businesses before the creator economy had a name.",
    "She began working with emerging digital creators at the beginning of the YouTube era, and went on to build creator careers, partnerships, businesses, IP and commercial opportunities across entertainment, media, brands and technology. The early roster included RCLBeauty101 (#1 most-viewed female channel worldwide), The Fine Brothers, Racka Racka and BlackBoxTV.",
    "Lennon Management was acquired in 2019.",
    "Today, Naomi continues to work selectively with creators through Lennon Management, while operating LENNON's broader creator partnerships and advisory business. Recent flagship work includes guiding Betül Tunç of Turkuaz Kitchen through an exclusive Anthropologie homeware collection and a New York Times Best Sellers cookbook with Ten Speed Press.",
  ],
};

// Industry service — peer-elected committee positions.
export const INDUSTRY_SERVICE = [
  { role: "Committee Member", body: "VidCon Hall of Fame Committee" },
  { role: "Panelist", body: "Streamy Awards Blue Ribbon Panel" },
];

// Selected clients & partners.
export const CLIENTS = [
  "P&G",
  "Anthropologie",
  "McDonald's",
  "PepsiCo",
  "COVERGIRL",
  "Ford",
  "Samsung",
  "KIA",
  "Unilever",
  "Tarte",
  "AT&T",
  "Universal Music",
  "Meta",
  "YouTube",
  "Ten Speed Press",
  "AliExpress",
  "New Form Digital",
  "Emergent",
];
